import { CHANNELS } from "../../shared/channels";
import { Reminder } from "../../shared/types";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { onViewShown } from "../navigation";
import { fillSharedBox, onSharedChange, readSharedAll } from "../shares";
import { locale, t } from "../i18n";

function kindText(kind: string): string {
  if (kind === "alarm") return `⏰ ${t("alarm")}`;
  if (kind === "timer") return `⏳ ${t("timer")}`;
  return t("reminder");
}

function whenText(item: Reminder): string {
  // shown as the clock time it was set for, in the zone it was set in
  const [day, clock] = item.at.split("T");
  const date = new Date(`${day}T12:00:00Z`);
  const dayText = Number.isNaN(date.getTime()) ? day : date.toLocaleDateString(locale(), { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" });
  const away = item.timeZone && item.timeZone !== Intl.DateTimeFormat().resolvedOptions().timeZone ? ` (${item.timeZone})` : "";
  return `${kindText(item.kind || "reminder")} · ${t(item.repeat)} · ${dayText} ${(clock || "").slice(0, 5)}${away}${item.done ? ` · ${t("done")}` : ""}`;
}

function buildItem(item: Reminder): HTMLElement {
  const row = fillFields(cloneTemplate("template-reminder"), { text: item.text, meta: whenText(item) });
  row.classList.toggle("done", item.done);
  row.querySelector("[data-action='remove']")!.addEventListener("click", async (event) => {
    event.stopPropagation();
    await call(CHANNELS.remindersRemove, item.id);
    reload();
  });
  return row;
}

// someone else's pings, shown read only with no remove button
function buildPeekItem(item: Reminder): HTMLElement {
  const row = fillFields(cloneTemplate("template-reminder"), { text: item.text, meta: whenText(item) });
  row.classList.toggle("done", item.done);
  row.querySelector("[data-action='remove']")?.remove();
  return row;
}

async function reloadPeeks(): Promise<void> {
  const people = await readSharedAll<Reminder[]>("pings", []);
  const upcoming = people.map((person) => ({ ...person, data: person.data.filter((item) => !item.done) }));
  fillSharedBox(byId("reminders-peek"), upcoming, "pings", buildPeekItem, t("nothing coming up"));
}

async function reload(): Promise<void> {
  const items = await call<Reminder[]>(CHANNELS.remindersList);
  renderList(byId("reminders-list"), items, buildItem);
  byId("reminders-empty").hidden = items.length > 0;
  await reloadPeeks();
}

export function setupRemindersView(): void {
  byId<HTMLFormElement>("reminders-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    const text = byId<HTMLInputElement>("reminders-text").value;
    const at = byId<HTMLInputElement>("reminders-at").value;
    const repeat = byId<HTMLSelectElement>("reminders-repeat").value as Reminder["repeat"];
    const kind = byId<HTMLSelectElement>("reminders-kind").value as Reminder["kind"];
    try {
      await call(CHANNELS.remindersAdd, text, at, repeat, kind, Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch (error) {
      flashToast(error instanceof Error ? error.message : String(error));
      return;
    }
    byId<HTMLInputElement>("reminders-text").value = "";
    reload();
  });
  listen(CHANNELS.remindersUpdated, () => void reload());
  onSharedChange("pings", () => void reloadPeeks());
  onViewShown("reminders", reload);
}
